// ─── sephoraAgent.js ──────────────────────────────────────────────────────────
// Agent for Sephora (via Anthropic mock).
// Outputs { item, brand, price, rating, inStock, store } + Sephora extras.
// ─────────────────────────────────────────────────────────────────────────────

const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate a Sephora API search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 38.00,
      "original_price": null,
      "rating": 4.6,
      "review_count": 8420,
      "sku_id": "P123456",
      "shade": null,
      "size": "1.0 oz / 30 mL",
      "seller_type": "Sephora",
      "is_rouge_deal": false,
      "rouge_label": null,
      "is_vib_deal": false,
      "vib_label": null,
      "is_beauty_insider": false,
      "loyalty_label": null,
      "is_sephora_collection": false,
      "is_clean_beauty": false,
      "is_vegan": false,
      "is_same_day": true,
      "is_store_pickup": true,
      "store_count": 680,
      "shipping_free": true,
      "shipping_cost": 0,
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL Sephora product names, brands, and realistic prices for "${query}"
- Brands: Charlotte Tilbury, Drunk Elephant, Tatcha, Sunday Riley, Fenty Beauty, NARS, Urban Decay, Too Faced, Rare Beauty, MAC, Dior Beauty, YSL Beauty, Lancome, Estee Lauder, Clinique, The Ordinary, Paula's Choice, La Mer, Glow Recipe, Kiehl's
- sku_id: format "P" + 6 digits
- shade: include for makeup (e.g. "Fair 10N", "Medium 220W"), null for skincare/haircare
- size: always include with both imperial and metric (e.g. "1.0 oz / 30 mL")
- is_rouge_deal: true for 1 premium item (Rouge member exclusive)
- is_clean_beauty: true for clean/natural brands (Drunk Elephant, Tatcha, Glow Recipe)
- is_vegan: true for 1 item if applicable
- is_sephora_collection: true for 1 item (Sephora Collection house brand, lower price)
- is_same_day: true for 2 items (same-day delivery)
- is_store_pickup: true for all (580+ Sephora stores)
- shipping_free: true for all (Sephora free shipping on orders $25+)
- Sephora skews premium — prices typically $25–$200+
- original_price: null for most (Sephora rarely discounts), higher for 1 sale item
- Price at mid / premium / luxury tiers for "${query}"`;
}

async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });
  if (!response.ok) throw new Error(`Sephora agent API error: ${response.status}`);
  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';
  const first   = rawText.indexOf('{');
  const last    = rawText.lastIndexOf('}');
  if (first === -1) throw new Error('Sephora agent: no JSON found');
  return JSON.parse(rawText.slice(first, last + 1));
}

function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100) : null;

  return {
    item:                  raw.title           || 'Unknown Product',
    brand:                 raw.brand           || 'Unknown',
    price,
    rating:                parseFloat(raw.rating) || 4.0,
    inStock:               raw.availability    !== 'Out of Stock',
    store:                 store.id,

    // Sephora-specific extras
    sku_id:                raw.sku_id          || null,
    shade:                 raw.shade           || null,
    size:                  raw.size            || null,
    original_price:        originalPrice,
    discount_pct:          discountPct,
    review_count:          parseInt(raw.review_count) || 0,
    seller_type:           'Sephora',
    is_rouge_deal:         raw.is_rouge_deal   === true,
    rouge_label:           raw.rouge_label     || null,
    is_vib_deal:           raw.is_vib_deal     === true,
    is_clean_beauty:       raw.is_clean_beauty === true,
    is_vegan:              raw.is_vegan        === true,
    is_sephora_collection: raw.is_sephora_collection === true,
    loyalty_label:         raw.loyalty_label   || null,
    is_same_day:           raw.is_same_day     === true,
    is_store_pickup:       raw.is_store_pickup === true,
    store_count:           parseInt(raw.store_count) || 0,
    shipping_free:         raw.shipping_free   === true,
    shipping_cost:         parseFloat(raw.shipping_cost) || 0,
    total_cost:            +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:           'sephora',
  };
}

export async function runSephoraAgent(store, query) {
  const prompt = buildPrompt(query);
  const parsed = await callAPI(prompt);
  const items  = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

export default runSephoraAgent;
